import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  compareSemver,
  DEVICE_REPORTING_SINCE_VERSION,
  parseEnterpriseClientDevicesResponse,
  type EnterpriseClientDevice,
  type EnterpriseClientDevicesResponse,
  type VersionLagEntry,
} from "@dosia/shared";
import { formatDateTime } from "../../lib/format.js";

/**
 * 员工列表「DOSIA 版本」列（2026-10-16 按设备记版本；N-176 叠加每日版本滞后名单）。
 *
 * 数据：dosia-enterprise 按设备记的版本（经 admin-portal `/client-devices` 透传），
 * 一人可能多台电脑——展示最近活跃那台，悬停看全部；低于最低推荐版本的标警示色
 * （员工侧栏此时正显示关不掉的升级提醒）。没有上报记录 = 老客户端或从未登录。
 *
 * N-176：同一响应带当天的版本滞后名单（`versionLag`）。老客户端不在设备表，名单按会话里自报的版本
 * 或「本周没有设备上报」把他们认出来——名单里的人按名单显示（版本 + 「低于推荐」+ 依据）；名单算出之后
 * 有设备上报到不低于门槛的版本，就以那次上报为准，不再挂旧标记。判定单源 {@link resolveDosiaVersionCellState}。
 */

export type DevicesByUser = Map<string, EnterpriseClientDevice[]> | "unavailable" | "loading";

/** 当天名单（按员工索引）。 */
export interface VersionLagByUser {
  byUser: Map<string, VersionLagEntry>;
  computedAt: string;
  floor: string;
}

/** 透传结果 → 每人设备列表（最近活跃在前）+ 最低推荐版本 + 当天名单。形状不对视为不可用。 */
export function useDevicesByUser(
  raw: unknown,
  loading: boolean,
  failed: boolean,
): { byUser: DevicesByUser; floor: string | null; lag: VersionLagByUser | null } {
  return useMemo(() => {
    if (failed) return { byUser: "unavailable" as const, floor: null, lag: null };
    if (raw === null || raw === undefined) {
      return { byUser: loading ? ("loading" as const) : ("unavailable" as const), floor: null, lag: null };
    }
    let parsed: EnterpriseClientDevicesResponse;
    try {
      parsed = parseEnterpriseClientDevicesResponse(raw);
    } catch {
      return { byUser: "unavailable" as const, floor: null, lag: null };
    }
    const byUser = new Map<string, EnterpriseClientDevice[]>();
    for (const d of parsed.devices) {
      const list = byUser.get(d.userId) ?? [];
      list.push(d);
      byUser.set(d.userId, list);
    }
    for (const list of byUser.values()) {
      list.sort((a, b) => Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt));
    }
    const report = parsed.versionLag;
    const lag = report
      ? {
          byUser: new Map(report.entries.map((e) => [e.userId, e])),
          computedAt: report.computedAt,
          floor: report.minRecommendedVersion,
        }
      : null;
    return { byUser, floor: parsed.minRecommendedVersion, lag };
  }, [raw, loading, failed]);
}

export type DosiaVersionCellState =
  | { kind: "loading" }
  | { kind: "unavailable" }
  | { kind: "none" }
  | { kind: "device"; devices: EnterpriseClientDevice[]; below: boolean }
  | { kind: "lag"; entry: VersionLagEntry; floor: string; computedAt: string; devices: EnterpriseClientDevice[] };

/** 一格怎么显示（纯函数）：名单优先，除非名单之后有设备上报到不低于门槛的版本。 */
export function resolveDosiaVersionCellState(input: {
  userId: string;
  byUser: DevicesByUser;
  floor: string | null;
  lag: VersionLagByUser | null;
}): DosiaVersionCellState {
  const { byUser, floor, lag, userId } = input;
  if (byUser === "loading") return { kind: "loading" };
  if (byUser === "unavailable") return { kind: "unavailable" };
  const devices = byUser.get(userId) ?? [];
  const entry = lag?.byUser.get(userId);
  if (lag && entry) {
    const computedMs = Date.parse(lag.computedAt);
    const upgradedSince = devices.some(
      (d) => Date.parse(d.lastSeenAt) > computedMs && (compareSemver(d.appVersion, lag.floor) ?? -1) >= 0,
    );
    if (!upgradedSince) return { kind: "lag", entry, floor: lag.floor, computedAt: lag.computedAt, devices };
  }
  if (devices.length === 0) return { kind: "none" };
  const below = floor !== null && (compareSemver(devices[0].appVersion, floor) ?? 0) < 0;
  return { kind: "device", devices, below };
}

const BASIS_KEY = {
  device: "admin.users.dosiaVersion.basisDevice",
  session: "admin.users.dosiaVersion.basisSession",
  "no-device-report": "admin.users.dosiaVersion.basisNoDeviceReport",
} as const;

export function DosiaVersionCell(props: {
  userId: string;
  byUser: DevicesByUser;
  floor: string | null;
  lag?: VersionLagByUser | null;
}): JSX.Element {
  const { t } = useTranslation();
  const state = resolveDosiaVersionCellState({ ...props, lag: props.lag ?? null });
  if (state.kind === "loading") return <span className="text-xs text-dosia-text-dim">…</span>;
  if (state.kind === "unavailable") {
    return (
      <span className="text-xs text-dosia-text-dim" title={t("admin.users.dosiaVersion.unavailable")}>
        —
      </span>
    );
  }
  if (state.kind === "none") {
    return (
      <span className="text-xs text-dosia-text-dim" title={t("admin.users.dosiaVersion.noneHint")}>
        {t("admin.users.dosiaVersion.none")}
      </span>
    );
  }
  const deviceLines = state.devices.map((d) =>
    t("admin.users.dosiaVersion.deviceLine", {
      version: d.appVersion,
      platform: d.platform,
      lastSeen: formatDateTime(d.lastSeenAt),
    }),
  );
  if (state.kind === "lag") {
    const { entry, floor, computedAt } = state;
    const tooltip = [
      t("admin.users.dosiaVersion.lagTooltip", { floor, computedAt: formatDateTime(computedAt) }),
      ...deviceLines,
    ].join("\n");
    return (
      <div data-testid="dosia-version-cell" className="flex flex-col gap-0.5" title={tooltip}>
        <span className="font-mono text-xs text-dosia-warning">
          {entry.version
            ? `v${entry.version}`
            : t("admin.users.dosiaVersion.lagInferred", { version: DEVICE_REPORTING_SINCE_VERSION })}
        </span>
        <span className="text-[10px] text-dosia-warning">
          {`${t("admin.users.dosiaVersion.belowFloor", { floor })} · ${t(BASIS_KEY[entry.basis])}`}
        </span>
      </div>
    );
  }
  const latest = state.devices[0];
  const below = state.below;
  return (
    <div data-testid="dosia-version-cell" className="flex flex-col gap-0.5" title={deviceLines.join("\n")}>
      <span className="flex items-baseline gap-1">
        <span className={`font-mono text-xs ${below ? "text-dosia-warning" : "text-dosia-text"}`}>
          v{latest.appVersion}
        </span>
        {state.devices.length > 1 ? (
          <span className="text-[10px] text-dosia-text-dim">
            {t("admin.users.dosiaVersion.moreDevices", { count: state.devices.length - 1 })}
          </span>
        ) : null}
      </span>
      {below ? (
        <span className="text-[10px] text-dosia-warning">
          {t("admin.users.dosiaVersion.belowFloor", { floor: props.floor })}
        </span>
      ) : (
        <span className="font-mono text-[10px] text-dosia-text-dim">{latest.platform}</span>
      )}
    </div>
  );
}
